import { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination, Table } from "rsuite";
import { Button } from "../../components/button.component";
import { PaginationEntriesInput } from "../../components/paginationEntries";
import { DateCell, TextCell } from "../../components/tableItem.component";
import { AppRouteApi } from "../../config/appRoute";
import {
  ActionsAppointmentTable,
  useAppDispatch,
  useAppSelector,
} from "../../core/redux";
import {
  IPaginationSearchFilter,
  useTablePaginationSearchFilter,
} from "../../hooks";

import { FcOk } from "react-icons/fc";
import {
  ApiSchemaAppointment,
  ApiSchemaFilteredAppointment,
} from "../../models/api";
import { ApiDataExchange } from "../../core/apis/dataExchange";

const { Column, HeaderCell, Cell } = Table;

interface IProps {
  selectedCompany: string;
  startDate: Date;
  endDate: Date;
}

export function AppointmentDataExchangeTable({
  selectedCompany,
  startDate,
  endDate,
}: IProps) {
  const dispatch = useAppDispatch();
  const { pageNumber, pageSize, nextPageToken, shouldRefetch } = useAppSelector(
    (state) => state.caregiverTable
  );
  const apiParams = useMemo<IPaginationSearchFilter["customParams"]>(() => {
    return {
      PageNumber: pageNumber,
      PageSize: pageSize,
      NextPageToken: nextPageToken,
      CompanyId: selectedCompany,
      StartDate: startDate?.toISOString(),
      EndDate: endDate?.toISOString(),
    };
  }, [
    pageNumber,
    pageSize,
    nextPageToken,
    selectedCompany,
    startDate,
    endDate,
  ]);
  const { apiData, refetch } = useTablePaginationSearchFilter<
    ApiSchemaFilteredAppointment,
    typeof ActionsAppointmentTable
  >(ActionsAppointmentTable, {
    customParams: apiParams,
    apiRoute: AppRouteApi.Appointment.Paginated(),
    requestType: "POST",
  });
  const [isSyncing, setIsSyncing] = useState({ status: false, id: "" });

  const handleSync = async (appointmentId: string, patientId: string) => {
    const data = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      caregiverId: "",
      patientId,
      companyId: selectedCompany,
    };
    await ApiDataExchange.SyncAppointment({
      data,
      onStart: () => setIsSyncing({ status: true, id: appointmentId }),
      onSuccess: () => refetch(),
      onEnd: () => setIsSyncing({ status: false, id: "" }),
    });
  };

  const onPageChange = useCallback(
    (value) => dispatch(ActionsAppointmentTable.setEntryCount(value)),
    [dispatch]
  );

  useEffect(() => {
    shouldRefetch && refetch();
  }, [shouldRefetch, refetch]);

  useEffect(() => {
    return () => {
      dispatch(ActionsAppointmentTable.resetData());
    };
  }, [dispatch]);

  return (
    <div>
      <div className="d-flex align-items-center mb-2">
        <div className="d-flex">
          <div className="align-self-center me-2">Show</div>
          <PaginationEntriesInput onChange={onPageChange} />
          <div className="align-self-center ms-2">entries</div>
        </div>
      </div>

      <Table
        autoHeight
        data={apiData?.list || []}
        loading={!apiData}
        className=""
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Appointment Available</h3>
          </div>
        )}
      >
        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Caregiver Name">Patient Name</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointment) => <p>{rowData.patientName}</p>}
          </Cell>
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Phone Number">Phone Number</span>
          </HeaderCell>
          <TextCell component="Appointment" dataKey="patientPhoneNumber" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Appointed Caregiver">Appointed Caregiver</span>
          </HeaderCell>
          <TextCell component="Appointment" dataKey="caregiverName" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Start Date">Start Date</span>
          </HeaderCell>
          <DateCell component="Appointment" dataKey="appointmentStartDate" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="End Date">End Date</span>
          </HeaderCell>
          <DateCell component="Appointment" dataKey="appointmentEndDate" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Status">Status</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointment) =>
              rowData.isDESynced ? (
                <FcOk size={30} />
              ) : (
                <Button
                  name="Sync Now"
                  className="btn-primary btn-sm"
                  onClick={() => handleSync(rowData.sKey, rowData.patientSKey)}
                  loading={isSyncing.status && rowData.sKey === isSyncing.id}
                />
              )
            }
          </Cell>
        </Column>
      </Table>

      {apiData?.list && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            total={apiData.totalCount || 0}
            pages={apiData?.list ? Math.ceil(apiData.totalCount / pageSize) : 0}
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page: any) =>
              dispatch(ActionsAppointmentTable.goToPage(page))
            }
          />
        </div>
      )}
    </div>
  );
}
